import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Card,
  CardBody,
  Box,
  Icon,
  useColorModeValue,
  InputGroup,
  InputLeftAddon,
  Badge,
  Checkbox,
  Divider,
  Collapse,
  Button,
  Flex,
} from '@chakra-ui/react';
import { FiBriefcase, FiMail, FiPhone, FiGlobe, FiMapPin, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { RegistrationData } from '../index';

// Geographic Data
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const CANADIAN_PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon'
];

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Brazil', 'Bulgaria', 'Cambodia',
  'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Czech Republic', 'Denmark', 'Ecuador',
  'Egypt', 'Estonia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan',
  'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg',
  'Malaysia', 'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Pakistan',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia',
  'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka',
  'Sweden', 'Switzerland', 'Taiwan', 'Thailand', 'Turkey', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Venezuela', 'Vietnam'
];

interface CompanyInformationProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const CompanyInformation: React.FC<CompanyInformationProps> = ({
  data,
  updateData,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const successBg = useColorModeValue('green.50', 'green.900');
  
  // State for conditional address fields
  const [showRemitAddress, setShowRemitAddress] = useState(false);
  const [showShipAddress, setShowShipAddress] = useState(false);
  const [vendorLocation, setVendorLocation] = useState(data.companyInfo.vendorLocation || '');

  const handleInputChange = (field: keyof typeof data.companyInfo, value: string) => {
    updateData({
      ...data,
      companyInfo: {
        ...data.companyInfo,
        [field]: value,
      },
    });
  };

  const isFormValid = () => {
    const { companyInfo } = data;
    return (
      companyInfo.name &&
      companyInfo.mainAddress &&
      companyInfo.phone &&
      companyInfo.email
    );
  };

  const getStateOptions = () => {
    if (vendorLocation === 'United States') return US_STATES;
    if (vendorLocation === 'Canada') return CANADIAN_PROVINCES;
    return [];
  };

  const getStateLabel = () => {
    if (vendorLocation === 'United States') return 'State';
    if (vendorLocation === 'Canada') return 'Province/Territory';
    return 'State/Region';
  };

  const getPostalLabel = () => {
    if (vendorLocation === 'United States') return 'ZIP Code';
    if (vendorLocation === 'Canada') return 'Postal Code';
    return 'Postal/ZIP Code';
  };

  return (
    <VStack spacing={8} align="stretch">
      {/* Basic Company Information */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiBriefcase} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Business Details
              </Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isRequired>
                <FormLabel>Company Name</FormLabel>
                <Input
                  value={data.companyInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your company name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Legal Name <Text as="span" color="gray.500" fontSize="sm">(if different)</Text></FormLabel>
                <Input
                  value={data.companyInfo.legalName || ''}
                  onChange={(e) => handleInputChange('legalName', e.target.value)}
                  placeholder="Legal business name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Business Type</FormLabel>
                <Select 
                  value={data.companyInfo.businessType || ''}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  placeholder="Select business type"
                >
                  <option value="Corporation">Corporation</option>
                  <option value="LLC">LLC</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="Non-profit">Non-Profit</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Federal Tax ID (EIN) <Text as="span" color="gray.500" fontSize="sm">(for W-9)</Text></FormLabel>
                <Input
                  value={data.companyInfo.ein || ''}
                  onChange={(e) => handleInputChange('ein', e.target.value)}
                  placeholder="XX-XXXXXXX"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Primary Location</FormLabel>
                <Select 
                  value={vendorLocation}
                  onChange={(e) => {
                    const value = e.target.value;
                    setVendorLocation(value);
                    handleInputChange('vendorLocation', value);
                  }}
                  placeholder="Select primary location"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="International">International</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Primary Product/Service</FormLabel>
                <Select 
                  value={data.companyInfo.productService || ''}
                  onChange={(e) => handleInputChange('productService', e.target.value)}
                  placeholder="Select your primary offering"
                >
                  <option value="Office Furniture">Office Furniture</option>
                  <option value="Healthcare Furniture">Healthcare Furniture</option>
                  <option value="Educational Furniture">Educational Furniture</option>
                  <option value="Hospitality Furniture">Hospitality Furniture</option>
                  <option value="Installation Services">Installation Services</option>
                  <option value="Maintenance Services">Maintenance Services</option>
                  <option value="Design Services">Design Services</option>
                  <option value="Other">Other</option>
                </Select>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiMail} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Contact Information
              </Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isRequired>
                <FormLabel>Primary Contact Name</FormLabel>
                <Input
                  value={data.companyInfo.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  placeholder="Full name of primary contact"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    <Icon as={FiMail} />
                  </InputLeftAddon>
                  <Input
                    type="email"
                    value={data.companyInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="company@example.com"
                  />
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    <Icon as={FiPhone} />
                  </InputLeftAddon>
                  <Input
                    value={data.companyInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Website</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    <Icon as={FiGlobe} />
                  </InputLeftAddon>
                  <Input
                    value={data.companyInfo.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="www.yourcompany.com"
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Fax Number <Text as="span" color="gray.500" fontSize="sm">(optional)</Text></FormLabel>
                <Input
                  value={data.companyInfo.fax || ''}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Main Business Address */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiMapPin} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Business Address
              </Text>
            </HStack>

            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Street Address</FormLabel>
                <Input
                  value={data.companyInfo.mainAddress || ''}
                  onChange={(e) => handleInputChange('mainAddress', e.target.value)}
                  placeholder="123 Business Street"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Suite / Apt. / Building</FormLabel>
                <Input
                  value={data.companyInfo.suite || ''}
                  onChange={(e) => handleInputChange('suite', e.target.value)}
                  placeholder="Suite 100"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={data.companyInfo.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{getStateLabel()}</FormLabel>
                  <Select
                    value={data.companyInfo.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder={`Select ${getStateLabel().toLowerCase()}`}
                  >
                    {getStateOptions().map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                    {vendorLocation === 'International' && <option value="Other">Other</option>}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{getPostalLabel()}</FormLabel>
                  <Input
                    value={data.companyInfo.postalCode || ''}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    placeholder={vendorLocation === 'United States' ? '90210' : vendorLocation === 'Canada' ? 'K1A 0A9' : 'Postal code'}
                  />
                </FormControl>
              </SimpleGrid>

              {vendorLocation === 'International' && (
                <FormControl isRequired>
                  <FormLabel>Country</FormLabel>
                  <Select
                    value={data.companyInfo.country || ''}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Select your country"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Additional Addresses */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">
              Additional Addresses <Text as="span" color="gray.500" fontSize="sm">(if different from business address)</Text>
            </Text>

            <VStack spacing={6} align="stretch">
              {/* Remit Address Toggle */}
              <Box>
                <Button
                  variant="ghost"
                  leftIcon={<Icon as={showRemitAddress ? FiChevronUp : FiChevronDown} />}
                  onClick={() => setShowRemitAddress(!showRemitAddress)}
                  size="md"
                  fontSize="md"
                  fontWeight="semibold"
                  justifyContent="flex-start"
                  p={2}
                >
                  Billing/Remit Address
                </Button>
                
                <Collapse in={showRemitAddress}>
                  <Box bg={sectionBg} p={6} borderRadius="md" mt={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Street Address</FormLabel>
                        <Input
                          value={data.companyInfo.remitAddress || ''}
                          onChange={(e) => handleInputChange('remitAddress', e.target.value)}
                          placeholder="Billing street address"
                        />
                      </FormControl>

                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <FormControl>
                          <FormLabel>City</FormLabel>
                          <Input placeholder="City" />
                        </FormControl>

                        <FormControl>
                          <FormLabel>{getStateLabel()}</FormLabel>
                          <Select placeholder={`Select ${getStateLabel().toLowerCase()}`}>
                            {getStateOptions().map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel>{getPostalLabel()}</FormLabel>
                          <Input placeholder="Postal code" />
                        </FormControl>
                      </SimpleGrid>

                      {vendorLocation === 'International' && (
                        <FormControl>
                          <FormLabel>Country</FormLabel>
                          <Select
                            value={data.companyInfo.remitCountry || ''}
                            onChange={(e) => handleInputChange('remitCountry', e.target.value)}
                            placeholder="Select country"
                          >
                            {COUNTRIES.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </VStack>
                  </Box>
                </Collapse>
              </Box>

              {/* Ship Address Toggle */}
              <Box>
                <Button
                  variant="ghost"
                  leftIcon={<Icon as={showShipAddress ? FiChevronUp : FiChevronDown} />}
                  onClick={() => setShowShipAddress(!showShipAddress)}
                  size="md"
                  fontSize="md"
                  fontWeight="semibold"
                  justifyContent="flex-start"
                  p={2}
                >
                  Shipping Address
                </Button>
                
                <Collapse in={showShipAddress}>
                  <Box bg={sectionBg} p={6} borderRadius="md" mt={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Street Address</FormLabel>
                        <Input
                          value={data.companyInfo.shipAddress || ''}
                          onChange={(e) => handleInputChange('shipAddress', e.target.value)}
                          placeholder="Shipping street address"
                        />
                      </FormControl>

                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <FormControl>
                          <FormLabel>City</FormLabel>
                          <Input placeholder="City" />
                        </FormControl>

                        <FormControl>
                          <FormLabel>{getStateLabel()}</FormLabel>
                          <Select placeholder={`Select ${getStateLabel().toLowerCase()}`}>
                            {getStateOptions().map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel>{getPostalLabel()}</FormLabel>
                          <Input placeholder="Postal code" />
                        </FormControl>
                      </SimpleGrid>

                      {vendorLocation === 'International' && (
                        <FormControl>
                          <FormLabel>Country</FormLabel>
                          <Select
                            value={data.companyInfo.shipCountry || ''}
                            onChange={(e) => handleInputChange('shipCountry', e.target.value)}
                            placeholder="Select country"
                          >
                            {COUNTRIES.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </VStack>
                  </Box>
                </Collapse>
              </Box>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Progress Indicator */}
      {isFormValid() && (
        <Card bg={successBg} borderColor="green.200">
          <CardBody>
            <HStack spacing={3}>
              <Icon as={FiBriefcase} color="green.500" />
              <Text fontSize="sm" color="green.700" fontWeight="medium">
                Company information completed
              </Text>
              <Badge colorScheme="green" ml="auto">
                Ready to continue
              </Badge>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default CompanyInformation; 